import {
  ApiOrganizationUserRolesRoleChoices,
  ApiRelationshipRelationshipChoices,
  CaregiverMetadataType,
  CaregiverType,
  ProviderType,
} from "@api/graphql/types-and-hooks";
import { CaregiverAppStatus, CodeSentVia, LinkedChild, MemberCaregiver } from "@pages/Members/types";

const BCBA_ROLES = new Set([
  ApiOrganizationUserRolesRoleChoices.Bcba,
  ApiOrganizationUserRolesRoleChoices.AssessmentBcba,
]);

export const buildBcbaNameByProviderId = (providers: (ProviderType | null | undefined)[]): Map<string, string> => {
  const lookup = new Map<string, string>();
  for (const provider of providers) {
    if (!provider) continue;
    const isBcba = provider.organizationuserrolesSet.edges.some((edge) => edge?.node && BCBA_ROLES.has(edge.node.role));
    if (!isBcba) continue;
    const name = `${provider.providermetadata?.firstName || ""} ${provider.providermetadata?.lastName || ""}`.trim();
    lookup.set(provider.id, name);
  }
  return lookup;
};

const RELATIONSHIP_LABELS: Record<ApiRelationshipRelationshipChoices, string> = {
  [ApiRelationshipRelationshipChoices.Bp]: "Billing contact person",
  [ApiRelationshipRelationshipChoices.Cp]: "Contact person",
  [ApiRelationshipRelationshipChoices.Ep]: "Emergency contact person",
  [ApiRelationshipRelationshipChoices.Other]: "Other",
};

const getRelationshipLabel = (relationship: ApiRelationshipRelationshipChoices): string =>
  RELATIONSHIP_LABELS[relationship] || relationship;

const deriveAppStatus = (caregivermetadata: CaregiverMetadataType | null | undefined): CaregiverAppStatus => {
  if (caregivermetadata?.appActivationCodeUsedAt) return CaregiverAppStatus.Connected;
  if (caregivermetadata?.activationCodeEmailLastSendDate || caregivermetadata?.activationCodeSmsLastSendDate) {
    return CaregiverAppStatus.CodeSent;
  }
  return CaregiverAppStatus.NotInvited;
};

const deriveCodeSentVia = (caregivermetadata: CaregiverMetadataType | null | undefined): CodeSentVia | undefined => {
  const hasEmail = !!caregivermetadata?.activationCodeEmailLastSendDate;
  const hasSms = !!caregivermetadata?.activationCodeSmsLastSendDate;
  if (hasEmail && hasSms) return CodeSentVia.Both;
  if (hasEmail) return CodeSentVia.Email;
  if (hasSms) return CodeSentVia.Sms;
  return undefined;
};

const deriveCodeSentDate = (caregivermetadata: CaregiverMetadataType | null | undefined): string | undefined => {
  const dates = [
    caregivermetadata?.activationCodeEmailLastSendDate,
    caregivermetadata?.activationCodeSmsLastSendDate,
  ].filter((d): d is string => !!d);
  if (dates.length === 0) return undefined;
  return dates.sort().at(-1);
};

const mapLinkedChildren = (
  relationshipSet: CaregiverType["relationshipSet"],
  bcbaNameByProviderId: Map<string, string>,
): LinkedChild[] =>
  relationshipSet.edges
    .map((edge) => edge?.node)
    .filter((node): node is NonNullable<typeof node> => !!node)
    .map((node) => {
      const learner = node.relationshipLearner;
      const bcbaName = learner.learnerproviderSet.edges
        .map((edge) => edge?.node?.provider?.id)
        .filter((providerId): providerId is string => !!providerId)
        .map((providerId) => bcbaNameByProviderId.get(providerId))
        .find((name): name is string => !!name);

      return {
        id: learner.id,
        name: `${learner.learnermetadata?.firstName || ""} ${learner.learnermetadata?.lastName || ""}`.trim(),
        dateOfBirth: learner.learnermetadata?.birthDate || "",
        assignedBcba: bcbaName || "",
        relationship: getRelationshipLabel(node.relationship),
      };
    });

export const mapCaregiverToMemberCaregiver = (
  caregiver: CaregiverType | null | undefined,
  bcbaNameByProviderId: Map<string, string>,
): MemberCaregiver | null => {
  if (!caregiver || !caregiver.caregivermetadata) return null;
  const { firstName, lastName, email, phoneNumber1 } = caregiver.caregivermetadata;

  const relationships = caregiver.relationshipSet.edges.map((edge) => edge?.node).filter(Boolean);
  const linkedChildren = mapLinkedChildren(caregiver.relationshipSet, bcbaNameByProviderId);

  return {
    id: caregiver.id,
    firstName,
    lastName,
    name: `${firstName} ${lastName}`.trim(),
    email: email || "",
    phone: phoneNumber1 || "",
    isPrimary: relationships.some((r) => r?.isPrimary),
    linkedChildren,
    // Nullable in the schema — the code is minted on demand when an admin sends it.
    activationCode: caregiver.caregivermetadata.appActivationCode ?? "",
    appStatus: deriveAppStatus(caregiver.caregivermetadata),
    codeSentDate: deriveCodeSentDate(caregiver.caregivermetadata),
    codeSentVia: deriveCodeSentVia(caregiver.caregivermetadata),
    connectedDate: caregiver.caregivermetadata.appActivationCodeUsedAt || undefined,
    assignedBcba: linkedChildren[0]?.assignedBcba || "",
  };
};
